import {Fragment, useCallback, useMemo, useState} from 'react';
import {Position, Tooltip as ReactTippy, Trigger} from 'react-tippy';

import {TooltipElement} from './TooltipElement';

import 'react-tippy/dist/tippy.css';
import './styles.scss';

type Props = {
    open?: boolean;
    header?: string | React.ReactNode;
    message?: string | React.ReactNode;
    position?: Position;
    trigger?: Trigger;
    children: React.ReactNode;
    className?: string;
    style?: {[key: string]: string};
    controlled?: boolean;
    overlay?: boolean;
    disabled?: boolean;
    distance?: number;
    color?: 'primary' | 'light';
    onOpen?: () => void;
    onClose?: () => void;
};

export const Tooltip: React.FC<Props> = ({
    open: openProp, header, message, children, position, trigger, controlled, overlay, disabled, className, distance = 15,
    color, style = {display: 'inline-flex'}, onOpen, onClose
}: Props) => {
    const [localOpen, setLocalOpen] = useState(false);
    const open = useMemo(() => openProp ?? localOpen, [openProp, localOpen]);
    const handleShow = useCallback(() => {
        setLocalOpen(true);
        onOpen?.();
    }, [setLocalOpen, onOpen]);

    const handleHide = useCallback(() => {
        setLocalOpen(false);
        onClose?.();
    }, [setLocalOpen, onClose]);

    if (disabled) {
        return <div style={style} className={className}>{children}</div>;
    }

    return (
        <Fragment>
            <ReactTippy
                position={position}
                trigger={trigger}
                interactive={controlled}
                arrow
                open={open}
                distance={distance}
                html={
                    <TooltipElement
                        header={header}
                        message={message}
                        controlled={controlled}
                        color={color}
                        onClose={handleHide}
                    />
                }
                className={className}
                style={style}
                onShow={handleShow}
                onHide={handleHide}
            >
                {children}
            </ReactTippy>
            {overlay && open && <div className='tw-inset-0 tw-fixed tw-opacity' onClick={handleHide} />}
        </Fragment>
    );
};